import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, Bell, ArrowLeft, Check, ChevronRight, ExternalLink } from 'lucide-react';
import { PregnancyInfoModal, usePregnancyInfo } from '@/features/pregnancy-info';
import { useNotifications, getNotificationTarget, type Notification } from '@/features/notifications';
import { cn } from '@/lib/utils';
import { isNative } from '@/lib/capacitor';

// Routes that are in the bottom navigation (no back button on these)
const MAIN_ROUTES = ['/', '/ssw', '/tools', '/chat', '/profile'];

// Special parent mappings for routes that don't follow URL hierarchy
const PARENT_ROUTE_MAP: Record<string, string> = {
  '/subscription': '/profile',
  '/partner': '/profile',
  '/notifications': '/',
  '/calendar': '/tools',
  '/blog': '/',
  // Legal pages should navigate back to profile
  '/legal/privacy': '/profile',
  '/legal/terms': '/profile',
  '/legal/imprint': '/profile',
};

// Prefix patterns for dynamic routes - checked in order
const PARENT_ROUTE_PREFIXES: { prefix: string; parent: string }[] = [
  { prefix: '/tools/product/', parent: '/tools/scanner' },
  { prefix: '/tools/scanner/', parent: '/tools/scanner' },
  { prefix: '/help/category/', parent: '/help' },
  { prefix: '/help/contact', parent: '/help' },
  { prefix: '/help/feedback', parent: '/help' },
];

/**
 * Gets the parent path for navigation.
 * Uses explicit mappings for special routes, otherwise derives from URL structure.
 */
function getParentPath(pathname: string): string {
  // Check for explicit mapping first
  if (PARENT_ROUTE_MAP[pathname]) {
    return PARENT_ROUTE_MAP[pathname];
  }

  // Check for prefix patterns (for dynamic routes like /tools/product/:barcode)
  for (const { prefix, parent } of PARENT_ROUTE_PREFIXES) {
    if (pathname.startsWith(prefix)) {
      return parent;
    }
  }

  // For hierarchical routes, remove the last segment
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length <= 1) {
    // Already at top level (e.g., /profile), go to home
    return '/';
  }

  // Remove last segment to get parent
  segments.pop();
  return '/' + segments.join('/');
}

interface TopBarProps {
  /** Force show/hide back button (overrides automatic detection) */
  showBackButton?: boolean;
  /** Custom back navigation (defaults to parent route) */
  onBack?: () => void;
}

export function TopBar({ showBackButton, onBack }: TopBarProps) {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const { hasData } = usePregnancyInfo();
  const location = useLocation();
  const navigate = useNavigate();

  // Determine if back button should be shown
  const isMainRoute = MAIN_ROUTES.includes(location.pathname);
  const shouldShowBack = showBackButton !== undefined ? showBackButton : !isMainRoute;
  const isHomePage = location.pathname === '/';

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Navigate to parent route instead of browser history
      const parentPath = getParentPath(location.pathname);
      navigate(parentPath);
    }
  };

  // Native App: Fixed container at top with safe area
  if (isNative) {
    return (
      <>
        {/* Fixed Top Container - transparent, sits at safe area edge */}
        <div
          className="fixed top-0 left-0 right-0 z-50"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left side: Logo (home only) or Back Button with glass effect */}
            <div className="flex items-center">
              {isHomePage ? (
                <Link to="/">
                  <img
                    src="https://parentpath.de/pp_logo-piktogram-w100.svg"
                    alt="ParentPath"
                    className="h-10 w-auto"
                  />
                </Link>
              ) : shouldShowBack ? (
                <button
                  onClick={handleBack}
                  className="glass-effect rounded-xl p-3 text-white hover:text-white/80 transition-colors"
                  aria-label="Zurück"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
              ) : (
                <div className="w-10" /> // Spacer
              )}
            </div>

            {/* Right side: TopNav Icons in glass container */}
            <div className="glass-effect rounded-xl px-3 py-2 flex items-center gap-4">
              {/* Pregnancy Info Card */}
              {hasData && (
                <button
                  onClick={() => setIsInfoModalOpen(true)}
                  className="p-1 text-white hover:text-white/80 transition-colors"
                  aria-label="Schwangerschafts-Infokarte"
                >
                  <ClipboardList className="w-5 h-5" />
                </button>
              )}

              {/* Notifications */}
              <NotificationBell
                isOpen={isNotificationOpen}
                onToggle={() => setIsNotificationOpen(!isNotificationOpen)}
                onClose={() => setIsNotificationOpen(false)}
                isNativeApp={true}
              />
            </div>
          </div>
        </div>

        {/* Pregnancy Info Modal */}
        <PregnancyInfoModal
          isOpen={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
        />
      </>
    );
  }

  // Web App: Original floating design
  return (
    <>
      {/* Back Button - show on sub-pages */}
      {shouldShowBack && (
        <button
          onClick={handleBack}
          className="fixed top-4 left-4 z-50 glass-effect rounded-xl p-3 text-white hover:text-white/80 transition-colors"
          aria-label="Zurück"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      )}

      {/* Floating Glass Container - Top Right */}
      <div className="fixed top-4 right-4 z-50 glass-effect rounded-xl px-3 py-2 flex items-center gap-4">
        {/* Pregnancy Info Card */}
        {hasData && (
          <button
            onClick={() => setIsInfoModalOpen(true)}
            className="p-1 text-white hover:text-white/80 transition-colors"
            aria-label="Schwangerschafts-Infokarte"
          >
            <ClipboardList className="w-5 h-5" />
          </button>
        )}

        {/* Notifications */}
        <NotificationBell
          isOpen={isNotificationOpen}
          onToggle={() => setIsNotificationOpen(!isNotificationOpen)}
          onClose={() => setIsNotificationOpen(false)}
          isNativeApp={false}
        />
      </div>

      {/* Pregnancy Info Modal */}
      <PregnancyInfoModal
        isOpen={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
      />
    </>
  );
}

// Notification Bell with Dropdown Modal
interface NotificationBellProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isNativeApp: boolean;
}

function NotificationBell({ isOpen, onToggle, onClose, isNativeApp }: NotificationBellProps) {
  const navigate = useNavigate();
  const {
    recentNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
  } = useNotifications();

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;

    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    onClose();

    // Get navigation target using helper function
    const target = getNotificationTarget(notification);
    if (target) {
      if (target.startsWith('http://') || target.startsWith('https://')) {
        window.open(target, '_blank', 'noopener,noreferrer');
      } else {
        navigate(target);
      }
    }
  };

  // Dropdown top position based on native/web
  const dropdownTop = isNativeApp
    ? 'calc(env(safe-area-inset-top, 0px) + 60px)'
    : '68px';

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={onToggle}
        className="relative p-1 text-white hover:text-white/80 transition-colors"
        aria-label="Benachrichtigungen"
        aria-expanded={isOpen}
      >
        <Bell className="w-5 h-5" />
        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-medium rounded-full">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Modal - rendered via portal to avoid TopNav stacking context */}
      {isOpen && createPortal(
        <>
          {/* Click-away backdrop - covers entire screen */}
          <div
            className="fixed inset-0 z-40"
            onClick={onClose}
          />

          {/* Dropdown - positioned below TopNav */}
          <div
            className="fixed right-4 w-[calc(100%-2rem)] max-w-85 rounded-[15px] overflow-hidden z-[9999] shadow-xl"
            style={{
              top: dropdownTop,
              background: 'rgba(31, 41, 55, 0.85)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/15">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-pp-orange" />
                <h3 className="font-semibold text-white">Benachrichtigungen</h3>
              </div>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllAsRead()}
                  className="flex items-center gap-1 text-xs text-white/60 hover:text-white font-medium"
                >
                  <Check className="w-3.5 h-3.5" />
                  Alle gelesen
                </button>
              )}
            </div>

            {/* Notifications List */}
            <div className="max-h-[300px] overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-white/60 text-sm">
                  Laden...
                </div>
              ) : recentNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-10 h-10 text-white/30 mx-auto mb-2" />
                  <p className="text-white/60 text-sm">
                    Keine Benachrichtigungen
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-white/10">
                  {recentNotifications.map((notification) => {
                    const target = getNotificationTarget(notification);
                    const isExternalLink = target?.startsWith('http://') || target?.startsWith('https://');

                    return (
                      <button
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification)}
                        className={cn(
                          'w-full flex items-start gap-3 p-3 text-left transition-colors hover:bg-white/10',
                          !notification.is_read && 'bg-white/5'
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            'text-sm text-pp-orange',
                            !notification.is_read ? 'font-semibold' : 'font-medium'
                          )}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-white line-clamp-2 mt-0.5">
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-white/60">
                              {formatRelativeTime(notification.created_at)}
                            </p>
                            {isExternalLink && (
                              <span className="inline-flex items-center gap-1 text-xs text-white/60">
                                <ExternalLink className="w-3 h-3" />
                                <span>Externer Link</span>
                              </span>
                            )}
                          </div>
                        </div>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-pp-orange rounded-full flex-shrink-0 mt-2" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer - always show to allow navigation to full notifications page */}
            <button
              onClick={() => {
                onClose();
                navigate('/notifications');
              }}
              className="flex items-center justify-center gap-1 w-full px-4 py-3 text-sm font-medium text-pp-orange hover:bg-white/10 border-t border-white/15 transition-colors"
            >
              Alle anzeigen
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
